/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {CHUNK_SIZE} from "../../resources-constants";
import {ApiConfig} from "../../../api";
import {
  AdnHttpCanceller,
  AdnHttpService,
  createAdnHttpCanceller
} from '../../../../util/adn-http.service';
import {
  AdnHttpSender,
  createAdnHttpResourceSender
} from '../../../../util/create-adn-http-resource';
import {AuthHttpInterceptorService} from '../../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../../app/common/directives/error/api-error-handler.service';

export interface StatsRequestState {
  canceller?: AdnHttpCanceller;
  isPending?: boolean;
}

@Injectable()
export class StatsResource {
  private readonly statsUri = ApiConfig.obtain().getVersionedUri('stats');
  private readonly statsTrafficUri = ApiConfig.obtain().getVersionedUri('stats/traffic');
  private readonly statsLocationUri = ApiConfig.obtain().getVersionedUri('stats/location');
  private readonly statsDomainUri = ApiConfig.obtain().getVersionedUri('stats/domain');
  private readonly statsTargetingUri = ApiConfig.obtain().getVersionedUri('stats/targeting');
  private readonly statsArticlesUri = ApiConfig.obtain().getVersionedUri('stats/article');
  private readonly predictTrafficUri = ApiConfig.obtain().getVersionedUri('stats/predict');
  private readonly cdnStatsUri = ApiConfig.obtain().getVersionedUri('stats/cdn');
  private readonly co2StatsUri = ApiConfig.obtain().getVersionedUri('stats/co2');
  private readonly defaultParams = {
    showFilteredBidReasons: true,
    groupBy: CHUNK_SIZE.DAILY.key
  };
  private readonly send: AdnHttpSender;

  constructor(
    AdnHttp: AdnHttpService,
    authHttpInterceptor: AuthHttpInterceptorService,
    ApiErrorHandler: ApiErrorHandlerService
  ) {
    this.send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  }

  private statsMeUp(url, params, cb, errCb, requestState?: any) {
    const state = requestState || {};

    if (state.canceller) {
      state.canceller.resolve("Cancelling previous stats request");
    }

    params = _.assign({}, this.defaultParams, params);
    if (params.startDate && params.startDate.toISOString) {
      params.startDate = params.startDate.toISOString();
    }
    if (params.endDate && params.endDate.toISOString) {
      params.endDate = params.endDate.toISOString();
    }
    if (params.idKey) {
      params[params.idKey] = params.id;
    }
    if (!params.groupBy) {
      delete params.groupBy;
    } else {
      params.groupBy = params.groupBy.toUpperCase();
    }
    params.includeChunkRange = true;

    if (!params.startDate && _.get(params, [params.idKey], "").indexOf(";") > -1) {
      return (errCb || _.noop)({invalidDate: true});
    }

    const canceller: AdnHttpCanceller = createAdnHttpCanceller();
    const request = {
      method: 'GET',
      url: url + '/',
      params: _.omit(params, ['id', 'idKey']),
      hasErrorHandler: _.isFunction(errCb),
      cancel: canceller.promise
    };
    state.canceller = canceller;

    state.isPending = true;
    return this.send(request).then(function (data) {
      state.isPending = false;
      return (cb || _.noop)(data);
    }).catch(function (response) {
      state.isPending = false;
      if (response.xhrStatus && response.xhrStatus.toLowerCase() === "abort") {
        return;
      }
      return (errCb || _.noop)(response);
    });
  }

  getPredict(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.predictTrafficUri, params, cb, errCb, requestState);
  }

  getCdn(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.cdnStatsUri, params, cb, errCb, requestState);
  }

  getCo2(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.co2StatsUri, params, cb, errCb, requestState);
  }

  getTraffic(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.statsTrafficUri + '/' + params.statTypeId, params, cb, errCb, requestState);
  }

  getLocation(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.statsLocationUri + '/' + params.statTypeId, params, cb, errCb, requestState);
  }

  getDomain(params, cb, errCb, requestState?: any) {
    const suffix = params.statTypeId ? "/" + params.statTypeId : "";
    if (!suffix) {
      params.groupBy = 'DOMAIN_NAME';
    }
    return this.statsMeUp(this.statsDomainUri + suffix, params, cb, errCb, requestState);
  }

  getTargeting(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.statsTargetingUri, params, cb, errCb, requestState);
  }

  getArticles(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.statsArticlesUri, params, cb, errCb, requestState);
  }

  getAsync(params, cb, errCb, requestState?: any) {
    const asyncSuffix = params && params.id ? '/' + params.id : '';
    return this.statsMeUp(this.statsUri + '/async' + asyncSuffix, params, cb, errCb, requestState);
  }

  get(params, cb, errCb, requestState?: any) {
    return this.statsMeUp(this.statsUri, params, cb, errCb, requestState);
  }
}
